import { useEffect, useState, useMemo, useCallback } from "react";
import { AlertCircle, Download } from "lucide-react";
import {
  fetchExpenseAudit,
  type YearlyExpenseAudit,
  type ExpenseLineAudit,
} from "../../api/client";
import { fmtCurrencyAcct, fmtPercent } from "../../utils/format";
import { prettyFieldLabel } from "../../utils/labels";
import { downloadCSV } from "../../utils/csv";
import LoadingSpinner from "../LoadingSpinner";

// ---------- types ----------

interface ExpenseTabProps {
  dealId: string;
  scenarioId: string;
}

// ---------- helpers ----------

function growthDiffers(
  yearData: YearlyExpenseAudit,
  rate: number | null,
): boolean {
  if (rate == null) return false;
  return Math.abs(rate - yearData.expense_growth_rate) > 0.0001;
}

function lineBgClass(
  lineName: string,
  yearData: YearlyExpenseAudit,
): string {
  const lower = lineName.toLowerCase();
  if (
    (lower.includes("tax") || lower === "real_estate_taxes") &&
    growthDiffers(yearData, yearData.tax_growth_rate)
  ) {
    return "bg-blue-50";
  }
  if (
    lower.includes("insurance") &&
    growthDiffers(yearData, yearData.insurance_growth_rate)
  ) {
    return "bg-purple-50";
  }
  return "";
}

/** Build a union of all line item names across all years, preserving order from year 1. */
function allLineNames(years: YearlyExpenseAudit[]): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const y of years) {
    for (const l of y.lines) {
      if (!seen.has(l.name)) {
        seen.add(l.name);
        names.push(l.name);
      }
    }
  }
  return names;
}

// ---------- Tooltip cell ----------

function CellWithTooltip({
  line,
  bgClass,
}: {
  line: ExpenseLineAudit | undefined;
  bgClass: string;
}) {
  const [showTip, setShowTip] = useState(false);

  if (!line) {
    return (
      <td className="px-2 py-2 text-[11px] text-bark-300 text-right tabular-nums whitespace-nowrap border-l border-grove-100">
        {"\u2014"}
      </td>
    );
  }

  return (
    <td
      className={`px-2 py-2 text-[11px] text-bark-700 text-right tabular-nums whitespace-nowrap border-l border-grove-100 relative cursor-default ${bgClass}`}
      onClick={() => setShowTip(!showTip)}
      onMouseLeave={() => setShowTip(false)}
    >
      {fmtCurrencyAcct(line.current_year_amount)}
      {showTip && line.formula && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-20 w-60 px-3 py-2 bg-bark-800 text-white text-[10px] rounded-lg shadow-lg pointer-events-none whitespace-normal">
          {line.formula}
        </div>
      )}
    </td>
  );
}

// ---------- Main component ----------

export default function ExpenseTab({ dealId, scenarioId }: ExpenseTabProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<YearlyExpenseAudit[]>([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchExpenseAudit(dealId, scenarioId);
      setData(result.sort((a, b) => a.year - b.year));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load expense audit");
    } finally {
      setLoading(false);
    }
  }, [dealId, scenarioId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const lineNames = useMemo(() => allLineNames(data), [data]);

  // Build a lookup: year -> lineName -> ExpenseLineAudit
  const lookup = useMemo(() => {
    const m = new Map<number, Map<string, ExpenseLineAudit>>();
    for (const yd of data) {
      const inner = new Map<string, ExpenseLineAudit>();
      for (const l of yd.lines) inner.set(l.name, l);
      m.set(yd.year, inner);
    }
    return m;
  }, [data]);

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 text-sm">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        {error}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white border border-grove-300 rounded-xl p-8 text-center">
        <p className="text-sm text-bark-400">No expense audit data available.</p>
      </div>
    );
  }

  function exportExpenseCSV() {
    const headers: (string | number)[] = ["Expense Line", ...data.map((yd) => `Yr ${yd.year} (${yd.calendar_year})`)];
    const rows: (string | number)[][] = lineNames.map((name) => [
      prettyFieldLabel(name),
      ...data.map((yd) => {
        const line = lookup.get(yd.year)?.get(name);
        return line ? Math.round(line.current_year_amount) : 0;
      }),
    ]);
    rows.push(["Total Above-Line", ...data.map((yd) => Math.round(yd.total_above_line))]);
    rows.push(["Total Operating Expenses", ...data.map((yd) => Math.round(yd.total_operating_expenses))]);
    downloadCSV([headers, ...rows], "audit_expenses.csv");
  }

  const thCls = "text-[11px] font-semibold text-bark-400 uppercase tracking-wider";
  const SW = "w-[180px] min-w-[180px]";
  const CW = "w-[110px] min-w-[110px]";

  return (
    <div className="space-y-6">
      {/* Download button */}
      <div className="flex justify-end print:hidden">
        <button
          onClick={exportExpenseCSV}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all"
        >
          <Download className="w-4 h-4" />
          CSV
        </button>
      </div>

      {/* Color legend */}
      <div className="flex flex-wrap gap-4 text-[11px] text-bark-500">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded bg-blue-50 border border-blue-200" />
          Tax growth differs from general
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded bg-purple-50 border border-purple-200" />
          Insurance growth differs from general
        </span>
      </div>

      <div className="bg-white border border-grove-300 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="border-collapse">
            <thead>
              {/* Year header row */}
              <tr className="bg-grove-100 border-b border-grove-300">
                <th className={`${SW} sticky left-0 z-10 bg-grove-100 px-3 py-2 text-left ${thCls}`}>
                  Expense Line
                </th>
                {data.map((yd) => (
                  <th
                    key={yd.year}
                    className={`${CW} px-2 py-2 text-right whitespace-nowrap border-l border-grove-300 ${thCls}`}
                  >
                    <div>Yr {yd.year} ({yd.calendar_year})</div>
                    <div className="mt-0.5 text-[10px] font-normal text-bark-400">
                      {fmtPercent(yd.expense_growth_rate)} growth
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lineNames.map((name) => (
                <tr key={name} className="border-b border-grove-200 last:border-0">
                  <td
                    className={`${SW} sticky left-0 z-10 bg-white px-3 py-2 text-[11px] font-medium text-bark-600 whitespace-nowrap border-r border-grove-200`}
                  >
                    {prettyFieldLabel(name)}
                  </td>
                  {data.map((yd) => {
                    const line = lookup.get(yd.year)?.get(name);
                    return (
                      <CellWithTooltip
                        key={`${name}-${yd.year}`}
                        line={line}
                        bgClass={lineBgClass(name, yd)}
                      />
                    );
                  })}
                </tr>
              ))}

              {/* Totals rows */}
              <tr className="border-t-2 border-grove-300 bg-grove-50">
                <td className={`${SW} sticky left-0 z-10 bg-grove-50 px-3 py-2 text-[11px] font-semibold text-bark-700 border-r border-grove-200`}>
                  Total Above-Line
                </td>
                {data.map((yd) => (
                  <td
                    key={yd.year}
                    className={`${CW} px-2 py-2 text-[11px] font-semibold text-bark-800 text-right tabular-nums whitespace-nowrap border-l border-grove-200`}
                  >
                    {fmtCurrencyAcct(yd.total_above_line)}
                  </td>
                ))}
              </tr>
              <tr className="bg-grove-50">
                <td className={`${SW} sticky left-0 z-10 bg-grove-50 px-3 py-2 text-[11px] font-bold text-forest-700 border-r border-grove-200`}>
                  Total Operating Expenses
                </td>
                {data.map((yd) => (
                  <td
                    key={yd.year}
                    className={`${CW} px-2 py-2 text-[11px] font-bold text-forest-700 text-right tabular-nums whitespace-nowrap border-l border-grove-200`}
                  >
                    {fmtCurrencyAcct(yd.total_operating_expenses)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
